/**
 * Mobile battlefield damage / health FX helpers.
 * Anchors must target `.order-design-card-bounds` (visible 200×100 tile), not outer
 * `[data-slot-zone]` flex wrappers — those are wider than the card on mobile rows.
 */

function escapeSelectorValue(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(String(value));
  }
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function collectBattlefieldSlotCandidates(zoneName, index) {
  if (zoneName == null || index == null || typeof document === 'undefined') {
    return [];
  }
  const selector = `[data-slot-zone="${escapeSelectorValue(zoneName)}"][data-slot-index="${String(index)}"]`;
  return [...document.querySelectorAll(selector)];
}

/**
 * @returns {HTMLElement|null}
 */
export function resolveBattlefieldSlotElement(zoneName, index) {
  const candidates = collectBattlefieldSlotCandidates(zoneName, index);
  let best = null;
  let bestArea = 0;

  for (const candidate of candidates) {
    const style = window.getComputedStyle(candidate);
    if (style.display === 'none' || style.visibility === 'hidden') {
      continue;
    }
    const rect = candidate.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      continue;
    }
    const area = rect.width * rect.height;
    if (area > bestArea) {
      bestArea = area;
      best = candidate;
    }
  }

  return best;
}

/**
 * Bounding rect for the deployed unit card art (matches StrategemTargetAnimation anchoring).
 * @returns {{ element: Element, rect: DOMRect }|null}
 */
export function resolveBattlefieldUnitCardRect(zoneName, index, instanceIdFallback = null) {
  if (zoneName == null || index == null || typeof document === 'undefined') {
    return null;
  }

  const idxStr = String(index);
  const zoneEsc = escapeSelectorValue(zoneName);

  const directCard =
    document.querySelector(
      `.deployed-unit-slot[data-slot-zone="${zoneEsc}"][data-slot-index="${idxStr}"] .order-design-card-bounds`
    ) ||
    document.querySelector(
      `[data-slot-zone="${zoneEsc}"][data-slot-index="${idxStr}"] .order-design-card-bounds`
    );

  if (directCard) {
    const rect = directCard.getBoundingClientRect();
    if (rect.width >= 2 && rect.height >= 2) {
      return { element: directCard, rect };
    }
  }

  let slotEl = document.querySelector(
    `.deployed-unit-slot[data-slot-zone="${zoneEsc}"][data-slot-index="${idxStr}"]`
  );
  if (!slotEl) {
    slotEl = document.querySelector(
      `[data-slot-zone="${zoneEsc}"][data-slot-index="${idxStr}"]`
    );
  }

  if (!slotEl && instanceIdFallback != null && instanceIdFallback !== '') {
    const suffix = `_${String(instanceIdFallback)}`;
    const nodes = document.querySelectorAll('[data-unit-key]');
    for (let i = 0; i < nodes.length; i += 1) {
      const key = nodes[i].getAttribute('data-unit-key') || '';
      if (key.endsWith(suffix)) {
        slotEl = nodes[i];
        break;
      }
    }
  }

  if (!slotEl) {
    return null;
  }

  const artEl =
    slotEl.querySelector('.order-design-minimal') ||
    slotEl.querySelector('.order-design-card-bounds');
  const src = artEl || slotEl;
  const rect = src.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) {
    return null;
  }

  return { element: src, rect };
}

/**
 * Anchor for opponent/player general portrait hits (OpponentHUD / PlayerHUD profile ring).
 * General attacks have no slot index — do not route through battlefield slot helpers.
 * @param {'opponent'|'your'|string} playerId
 */
export function getMobileGeneralDamageAnchorFromDom(playerId) {
  if (playerId == null || typeof document === 'undefined') {
    return null;
  }
  const generalZone = playerId === 'opponent' ? 'opponent_general' : 'player_general';
  const element = document.querySelector(`[data-zone="${generalZone}"]`);
  if (!element) {
    return null;
  }
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') {
    return null;
  }
  const rect = element.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) {
    return null;
  }
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
    slotElement: element,
    rect,
  };
}

export function getMobileSlotDamageAnchorFromDom(zoneName, index, instanceIdFallback = null) {
  const resolved = resolveBattlefieldUnitCardRect(zoneName, index, instanceIdFallback);
  if (!resolved) {
    const slotEl = resolveBattlefieldSlotElement(zoneName, index);
    if (!slotEl) {
      return null;
    }
    const rect = slotEl.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
      slotElement: slotEl,
      rect,
    };
  }

  const { element, rect } = resolved;
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
    slotElement: element,
    rect,
  };
}

/** Relaxed viewport bounds for scaled mobile slots (generals / edge orbs may sit near y=0). */
export function isValidMobileDamageAnimationAnchor(anchor, { isGeneralAnimation = false } = {}) {
  if (!anchor || typeof anchor.x !== 'number' || typeof anchor.y !== 'number') {
    return false;
  }
  const { x, y } = anchor;
  if (Number.isNaN(x) || Number.isNaN(y)) {
    return false;
  }
  const minY = isGeneralAnimation ? -24 : 0;
  if (x <= 0 || y < minY) {
    return false;
  }
  if (typeof window !== 'undefined') {
    const maxX = window.innerWidth + 64;
    const maxY = window.innerHeight + 64;
    if (x > maxX || y > maxY) {
      return false;
    }
  }
  return true;
}

/** Default portaled damage burst container size from viewport scale. */
export function getMobileDamageAnimationSizePx(viewportScale = 1) {
  const scale = viewportScale > 0 ? viewportScale : 1;
  return Math.max(72, Math.min(136, Math.round(192 * scale)));
}

/** Size the burst container to the on-screen card tile when rect is known. */
export function resolveMobileDamageAnimationContainerSizePx(rect, viewportScale = 1) {
  if (rect && rect.width > 0 && rect.height > 0) {
    const cardSpan = Math.max(rect.width, rect.height);
    return Math.min(136, Math.max(72, Math.round(cardSpan * 1.08)));
  }
  return getMobileDamageAnimationSizePx(viewportScale);
}

export function resolveMobileHealthFlyoutDurationMs(durationMs, viewportScale = 1) {
  const scale = viewportScale > 0 ? viewportScale : 1;
  return Math.max(Math.round(durationMs * 0.92), Math.round(180 / scale));
}

/** Mobile: anchor burst at card center (desktop uses random off-center presets). */
export const MOBILE_DAMAGE_INDICATOR_POSITION = Object.freeze({ x: 50, y: 50 });
